import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { useApp } from "../context/AppContext";

const Shop = () => {
  const { lang, t } = useApp();
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const cat = params.get("cat") || "";

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    let active = true;
    const fetch = async () => {
      setLoading(true);
      const q = cat ? `?category=${cat}` : "";
      const r = await api.get(`/products${q}`);
      if (!active) return;
      setProducts(r.data);
      setLoading(false);
    };
    fetch();
    return () => { active = false; };
  }, [cat]);

  const parents = categories.filter((c) => c.active && !c.parent_slug).sort((a, b) => a.order - b.order);
  const childrenOf = (slug) => categories.filter((c) => c.active && c.parent_slug === slug).sort((a, b) => a.order - b.order);
  const currentCat = categories.find((c) => c.slug === cat);
  // If current cat is a child, expand its parent in sidebar
  const expandedParent = currentCat?.parent_slug || (currentCat && !currentCat.parent_slug ? currentCat.slug : "");

  const setCat = (slug) => setParams(slug ? { cat: slug } : {});

  const heading = (() => {
    if (!currentCat) return lang === "id" ? "Semua Produk" : "All Products";
    return lang === "id" ? currentCat.name_id : currentCat.name_en;
  })();

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
      <div className="text-center mb-10">
        <div className="text-[11px] tracking-[0.3em] uppercase text-holy-gold-dark font-semibold mb-3">
          ✦ Holy Flowy Shop ✦
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-holy-red mb-3" data-testid="shop-heading">{heading}</h1>
        <p className="font-script text-2xl text-foreground/70">{t("handmade_with_love")}</p>
      </div>

      <div className="grid lg:grid-cols-[240px,1fr] gap-8">
        {/* Sidebar */}
        <aside className="lg:sticky lg:top-24 lg:self-start" data-testid="shop-sidebar">
          <div className="bg-cream rounded-2xl p-5 border border-border/50">
            <div className="text-[11px] tracking-[0.3em] uppercase text-holy-gold-dark font-semibold mb-3">
              {lang === "id" ? "Kategori" : "Categories"}
            </div>
            <button
              onClick={() => setCat("")}
              data-testid="filter-all"
              className={`w-full text-left text-sm py-2 px-3 rounded-lg mb-1 transition-colors ${
                cat === "" ? "bg-holy-red text-ivory" : "text-foreground hover:bg-ivory"
              }`}
            >
              ✦ {lang === "id" ? "Semua Produk" : "All Products"}
            </button>
            <ul className="space-y-0.5">
              {parents.map((p) => {
                const subs = childrenOf(p.slug);
                const isExpanded = expandedParent === p.slug;
                const isActive = cat === p.slug;
                return (
                  <li key={p.id}>
                    <button
                      onClick={() => setCat(p.slug)}
                      data-testid={`filter-${p.slug}`}
                      className={`w-full text-left text-sm py-2 px-3 rounded-lg transition-colors flex items-center justify-between ${
                        isActive ? "bg-holy-red text-ivory" : "text-foreground hover:bg-ivory"
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        {p.image_url ? (
                          <img src={p.image_url} alt="" className="w-6 h-6 rounded-full object-cover" />
                        ) : (
                          <span>{p.icon}</span>
                        )}
                        {lang === "id" ? p.name_id : p.name_en}
                      </span>
                      {subs.length > 0 && <span className="text-[10px] opacity-60">{subs.length}</span>}
                    </button>
                    {isExpanded && subs.length > 0 && (
                      <ul className="ml-5 mt-1 mb-2 border-l border-border/60 pl-2 space-y-0.5">
                        {subs.map((s) => (
                          <li key={s.id}>
                            <button
                              onClick={() => setCat(s.slug)}
                              data-testid={`filter-${s.slug}`}
                              className={`w-full text-left text-xs py-1.5 px-2 rounded-md transition-colors flex items-center gap-1.5 ${
                                cat === s.slug ? "bg-holy-gold/20 text-holy-red font-medium" : "text-muted-foreground hover:text-holy-red hover:bg-ivory"
                              }`}
                            >
                              {s.image_url ? (
                                <img src={s.image_url} alt="" className="w-4 h-4 rounded-full object-cover" />
                              ) : (
                                <span>{s.icon}</span>
                              )}
                              {lang === "id" ? s.name_id : s.name_en}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </aside>

        {/* Products grid */}
        <div>
          {loading ? (
            <div className="text-center py-20 text-muted-foreground">Loading…</div>
          ) : products.length === 0 ? (
            <div className="text-center py-20 text-muted-foreground" data-testid="shop-empty">
              {lang === "id" ? "Belum ada produk di kategori ini." : "No products in this category yet."}
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-testid="shop-grid">
              {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Shop;
